import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { initialState } from './flag.state';
import { AlertService, Environment, Flag, FLAG_MESSAGES, GET_ERRORS, PUBLISH_MESSAGES } from '@ffx/common';
import { effect } from '@angular/core';
import { inject } from '@angular/core';
import { take, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@ffx/env';
import { UpdateFeatureFlexLambdaInterface } from '../interfaces/lambda-interfaces';
import { FlagsService } from '../services/flags/flags.service';
import { getFlagArrayDifferences } from '../utils/flags.functions';
import { DbProject } from '../interfaces/db-interfaces';

export const FlagStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => {
        const alertService = inject(AlertService);
        const http = inject(HttpClient);
        const flagService = inject(FlagsService);

        return {
            clearStore(): void {
                patchState(store, {
                    ...initialState
                });
            },
            setEnvironmentsAndSelectedEnvironmentId(environments: Environment[], selectedEnvironmentId: string): void {
                patchState(store, {
                    environments: environments,
                    selectedEnvironmentId
                });
            },
            setTriggerRefreshFlagsEffect(): void {
                patchState(store, {
                    triggerRefreshFlagsEffect: true
                });
            },
            setSelectedEnvironmentId(selectedEnvironmentId: string): void {
                patchState(store, {
                    selectedEnvironmentId
                });
            },
            async updateOriginalAndPublishFlags(
                newFlags: Flag[],
                selectedProject: DbProject,
                selectedEnvironment: Environment
            ): Promise<void> {
                try {
                    patchState(store, { publishFlagsLoading: true });
                    const oldFlag = store.environmentFlags().flags;
                    const publishFlagObject = getFlagArrayDifferences(oldFlag, newFlags);
                    const environments = store.environments();

                    if (!environments.length) {
                        const warningMessage = PUBLISH_MESSAGES.NO_ENV;
                        alertService.showWarningAlert(warningMessage);
                        patchState(store, { errorMessage: warningMessage });
                        return;
                    }

                    const flagsToAdd: Flag[] = [];
                    const flagsToUpdate: Flag[] = [];

                    publishFlagObject.flagsToUpdateAdd.forEach((flag: Flag) => {
                        if (flag.id) {
                            flagsToUpdate.push(flag);
                        } else {
                            flagsToAdd.push(flag);
                        }
                    });

                    try {
                        await Promise.all([
                            flagService
                                .addFlags(selectedProject, environments, flagsToAdd, selectedEnvironment)
                                .pipe(take(1))
                                .toPromise(),
                            flagService.updateFlags(selectedProject.groups, flagsToUpdate).pipe(take(1)).toPromise(),
                            flagService.deleteFlags(publishFlagObject.flagsToDelete).pipe(take(1)).toPromise()
                        ]);

                        const flagsObject = newFlags.reduce((tempObject: any, flag: Flag) => {
                            tempObject[flag.key] = flag.status;
                            return tempObject;
                        }, {});

                        const flagsAndEnvironment: UpdateFeatureFlexLambdaInterface = {
                            flags: flagsObject,
                            environmentId: selectedEnvironment.id,
                            projectId: selectedProject.id
                        };

                        const url = `/api/rest/${environment.updateFlagsEndpoint}`;
                        const responseData: any = await firstValueFrom(http.post(url, flagsAndEnvironment));

                        patchState(store, {
                            publishFlagsLoading: false,
                            environmentFlags: flagsObject,
                            triggerRefreshFlagsEffect: true
                        });
                        alertService.showPositiveAlert(
                            responseData.message || PUBLISH_MESSAGES.PUBLISH_FLAGS_SUCCESSFUL
                        );
                    } catch {
                        const errorMessage = PUBLISH_MESSAGES.PUBLISH_FLAGS_FAILED;
                        alertService.showNegativeAlert(errorMessage);
                        patchState(store, {
                            errorMessage,
                            publishFlagsLoading: false,
                            environmentFlags: undefined,
                            triggerRefreshFlagsEffect: true
                        });
                    }
                } catch {
                    const errorMessage = PUBLISH_MESSAGES.PUBLISH_FLAGS_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                    patchState(store, {
                        errorMessage,
                        publishFlagsLoading: false,
                        environmentFlags: undefined,
                        triggerRefreshFlagsEffect: true
                    });
                }
            },
            async getFlags(): Promise<void> {
                try {
                    patchState(store, {
                        flagsLoading: true,
                        triggerRefreshFlagsEffect: true
                    });

                    const selectedEnvironmentId = store.selectedEnvironmentId();
                    const selectedEnvironment = store
                        .environments()
                        .find((environment) => environment.id === selectedEnvironmentId);

                    if (selectedEnvironmentId && selectedEnvironment) {
                        try {
                            const flags = await firstValueFrom(
                                flagService.getFeatureFlagsByEnvironmentId(selectedEnvironmentId)
                            );
                            patchState(store, {
                                flagsLoading: false,
                                triggerRefreshFlagsEffect: false,
                                environmentFlags: {
                                    environment: selectedEnvironment,
                                    flags: flags.map((flag: any) => ({
                                        ...flag,
                                        status: flag.isOn
                                    }))
                                }
                            });
                        } catch {
                            const errorMessage = GET_ERRORS.ENVIRONMENT_DETAILS;
                            patchState(store, {
                                errorMessage,
                                flagsLoading: false,
                                triggerRefreshFlagsEffect: false
                            });
                            alertService.showNegativeAlert(errorMessage);
                        }
                    }
                } catch {
                    const errorMessage = FLAG_MESSAGES.GET_FLAGS_FAILED;
                    patchState(store, {
                        errorMessage,
                        flagsLoading: false,
                        triggerRefreshFlagsEffect: false
                    });
                    alertService.showNegativeAlert(errorMessage);
                }
            }
        };
    }),
    withHooks({
        onInit(store) {
            effect(
                () => {
                    if (store.triggerRefreshFlagsEffect()) {
                        store.getFlags();
                    }
                },
                {
                    allowSignalWrites: true
                }
            );
        }
    })
);
